import * as React from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import {
  SlideDrawer,
  type SlideDrawerProps,
  type SlideDrawerSize,
} from "@/components/ui/slide-drawer";

/**
 * FormDrawer
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.7 + §3.7
 *
 * A `<SlideDrawer>` with a pre-wired footer:
 *   [ Cancel (outline, left) ]                [ Save (default, right) ]
 *
 * The Save button is a `<button type="submit" form={formId}>` so the actual
 * form lives inside the drawer body and the footer submit triggers it via
 * native form submission. This lets us keep the form markup and the submit
 * button in separate scroll containers.
 *
 * Showing progress:
 *   - `isPending` → submit shows `<Loader2 className="animate-spin" />`
 *     prefixed to its existing label and disables itself.
 *   - `isUploading` → optional progress bar above the footer (used by Files
 *     section uploads writing to R2). Pair with a numeric `uploadProgress`
 *     (0-100) for the actual width.
 *
 * The drawer is non-dismissable on overlay click by default — users won't
 * lose form state from a stray outside click. The X button in the header is
 * still wired through.
 *
 * Pass `submitVariant="destructive"` for delete-confirmation drawers (rare;
 * usually a centered AlertDialog is more appropriate — see §2.8).
 */
export interface FormDrawerProps
  extends Omit<SlideDrawerProps, "footer" | "children" | "dismissOnOverlayClick"> {
  /** The id attribute on the inner `<form>` so the submit button can target it. */
  formId: string;
  isPending?: boolean;
  isUploading?: boolean;
  /** 0-100 numeric width for the uploading progress bar. */
  uploadProgress?: number;
  submitLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  submitVariant?: "default" | "destructive";
  /** Disable the submit button even when not pending — useful for invalid forms. */
  submitDisabled?: boolean;
  /** Hide the Cancel button entirely (rare — single-action drawers). */
  hideCancel?: boolean;
  /** Extra slot rendered between Cancel and Submit in the footer row. */
  footerExtra?: React.ReactNode;
  size?: SlideDrawerSize;
  children: React.ReactNode;
}

export function FormDrawer({
  formId,
  isPending = false,
  isUploading = false,
  uploadProgress,
  submitLabel = "Save Changes",
  cancelLabel = "Cancel",
  submitVariant = "default",
  submitDisabled = false,
  hideCancel = false,
  footerExtra,
  onOpenChange,
  children,
  ...rest
}: FormDrawerProps) {
  return (
    <SlideDrawer
      {...rest}
      onOpenChange={onOpenChange}
      dismissOnOverlayClick={false}
      footer={
        <>
          {/* Optional R2 upload progress bar above the action row */}
          {isUploading ? (
            <div className="mb-3" aria-live="polite">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>Uploading…</span>
                {typeof uploadProgress === "number" ? (
                  <span>{Math.round(uploadProgress)}%</span>
                ) : null}
              </div>
              <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={cn(
                    "h-full bg-primary transition-[width] duration-200 ease-out",
                    typeof uploadProgress !== "number" && "animate-pulse"
                  )}
                  style={{
                    width:
                      typeof uploadProgress === "number"
                        ? `${Math.min(100, Math.max(0, uploadProgress))}%`
                        : "40%",
                  }}
                />
              </div>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            {!hideCancel ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending || isUploading}
                data-testid="button-cancel"
              >
                {cancelLabel}
              </Button>
            ) : null}
            {footerExtra}
            <Button
              type="submit"
              form={formId}
              variant={submitVariant}
              disabled={submitDisabled || isPending || isUploading}
              data-testid="button-submit"
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {submitLabel}
            </Button>
          </div>
        </>
      }
    >
      {children}
    </SlideDrawer>
  );
}
